import { supabase } from '@/integrations/supabase/client';

interface CacheInfo {
  timestamp: number;
  version: string;
  data: any;
}

class CacheManager {
  private static instance: CacheManager;
  private cacheVersion = '1.0.0';
  private maxCacheAge = 5 * 60 * 1000; // 5 minutos

  static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  // Verificar se o cache está válido
  isCacheValid(key: string): boolean {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return false;

      const cacheInfo: CacheInfo = JSON.parse(cached);
      const now = Date.now();

      // Verificar se o cache não expirou
      if (now - cacheInfo.timestamp > this.maxCacheAge) {
        this.removeCache(key);
        return false;
      }

      // Verificar se a versão é compatível
      if (cacheInfo.version !== this.cacheVersion) {
        this.removeCache(key);
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Erro ao verificar cache:', error);
      this.removeCache(key);
      return false;
    }
  }

  // Salvar dados no cache
  setCache(key: string, data: any): void {
    try {
      const cacheInfo: CacheInfo = {
        timestamp: Date.now(),
        version: this.cacheVersion,
        data
      };
      localStorage.setItem(key, JSON.stringify(cacheInfo));
    } catch (error) {
      console.warn('Erro ao salvar cache:', error);
    }
  }

  // Obter dados do cache
  getCache(key: string): any | null {
    try {
      if (!this.isCacheValid(key)) return null;

      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheInfo: CacheInfo = JSON.parse(cached);
      return cacheInfo.data;
    } catch (error) {
      console.warn('Erro ao obter cache:', error);
      this.removeCache(key);
      return null;
    }
  }

  // Remover cache específico
  removeCache(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Erro ao remover cache:', error);
    }
  }

  // Limpar todo o cache
  clearAllCache(): void {
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log('Cache limpo com sucesso');
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
    }
  }

  // Detectar problemas de cache
  detectCacheIssues(): boolean {
    try {
      // Verificar se há dados corrompidos no localStorage
      const keys = Object.keys(localStorage);
      let hasIssues = false;

      for (const key of keys) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            JSON.parse(value);
          }
        } catch (error) {
          console.warn('Dados corrompidos encontrados no cache:', key);
          hasIssues = true;
          this.removeCache(key);
        }
      }

      return hasIssues;
    } catch (error) {
      console.error('Erro ao detectar problemas de cache:', error);
      return true;
    }
  }

  // Forçar refresh completo
  async forceRefresh(): Promise<void> {
    try {
      // Limpar cache do navegador
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Limpar cache local
      this.clearAllCache();

      // Limpar sessão do Supabase
      await supabase.auth.signOut();

      // Recarregar página
      window.location.reload();
    } catch (error) {
      console.error('Erro ao forçar refresh:', error);
      window.location.reload();
    }
  }

  // Verificar se há problemas de conectividade
  async checkSupabaseConnection(): Promise<boolean> {
    try {
      // Usar uma URL pública para testar conectividade geral
      const response = await fetch('https://httpbin.org/status/200', {
        method: 'HEAD',
        cache: 'no-cache'
      });
      return response.ok;
    } catch (error) {
      // CORS pode bloquear, mas isso não significa que não há conectividade
      console.warn('Teste de conectividade falhou (possível CORS):', error);
      return true; // Assumir que está ok se não conseguirmos testar
    }
  }
}

export const cacheManager = CacheManager.getInstance();

// Função para limpar cache automaticamente se necessário
export const autoClearCacheIfNeeded = (): void => {
  if (cacheManager.detectCacheIssues()) {
    console.log('Problemas de cache detectados, limpando...');
    cacheManager.clearAllCache();
  }
};

// Função para verificar conectividade e cache
export const checkSystemHealth = async (): Promise<{
  cacheHealthy: boolean;
  connectivityHealthy: boolean;
}> => {
  const cacheHealthy = !cacheManager.detectCacheIssues();
  const connectivityHealthy = await cacheManager.checkSupabaseConnection();

  return {
    cacheHealthy,
    connectivityHealthy
  };
};
